import React from 'react';
import {
  MODEL__NAME__ACCOUNT,
  MODEL__NAME__CANDIDATE,
  MODEL__NAME__EMPLOYER,
  MODEL__NAME__ENGAGEMENT,
  MODEL__NAME__JOB
} from '../../dictionaries/Models.dic';
import {
  RELATION__ACCOUNTS_STARRED,
  RELATION__CANDIDATES_STARRED,
  RELATION__EMPLOYERS_STARRED,
  RELATION__ENGAGEMENTS_STARRED,
  RELATION__JOBS_STARRED
} from '../../dictionaries/Relations.dic';
import {
  NOT,
  YES
} from '../../lib/Boolean.lib';
import Core from '../../lib/Core';
import globalErrorHandler from '../../lib/Error/globalErrorHandler.fun';
import useState from '../../lib/hooks/useState.hook';
import {
  getAccountModel
} from '../../lib/models/account';
import {
  getCandidateModel
} from '../../lib/models/candidate';
import getEmployerModel from '../../lib/models/employer';
import {
  getEngagementModel
} from '../../lib/models/engagement';
import {
  getJobModel
} from '../../lib/models/job';
import {
  Obj
} from '../../lib/Object.lib';
import orderJsonProperties from '../../lib/Object/orderJsonProperties.fun';
import {
  COLLECTION__ACCOUNTS,
  COLLECTION__CANDIDATES,
  COLLECTION__EMPLOYERS,
  COLLECTION__ENGAGEMENTS,
  COLLECTION__JOBS,
  readLoopbackRecord
} from '../../lib/services/BE/loopback.api';
import {
  joinKeyName,
  Str,
  trim
} from '../../lib/String.lib';
import Box from '../Layout/Wrappers/Box';
import Button from '../Layout/Wrappers/Button';
import Checkbox from '../Layout/Wrappers/Checkbox';
import Dialog from '../Layout/Wrappers/Dialog';
import Fieldset from '../Layout/Wrappers/Fieldset';
import JsonView from '../Layout/Wrappers/JsonView';
import {
  SuccessChipMessage,
  WarningChipMessage
} from '../Layout/Wrappers/Message';
import Once from '../Layout/Wrappers/Once';
import TextField from '../Layout/Wrappers/TextField';
import Title from '../Layout/Wrappers/Title';
import Typography, {
  Label
} from '../Layout/Wrappers/Typography';

const KEY__CONTROLLER_NAME__SAMPLE_JSON_DATA = 'SampleJsonData';

export function getControllerSampleJsonData() {
  return Obj(Core.getKeyValue(KEY__CONTROLLER_NAME__SAMPLE_JSON_DATA));
}

export function setControllerSampleJsonData(controller) {
  return Core.setKeyValue(KEY__CONTROLLER_NAME__SAMPLE_JSON_DATA, controller);
}

export default function SampleJsonData({
  global = false
}) {
  const mem = React.useRef({});
  const [state, updateState] = useState();

  const VALUE__ID = Str(state.id);
  const VALUE__INPUT = Str(state.valueInput);
  const VALUE__COLLECTION = Str(state.collection);
  const JSON_DATA_A = state.jsonDataA || [];
  const JSON_DATA_B = state.jsonDataB || [];

  const NAME__MODEL = {
    [COLLECTION__ACCOUNTS]: MODEL__NAME__ACCOUNT,
    [COLLECTION__CANDIDATES]: MODEL__NAME__CANDIDATE,
    [COLLECTION__EMPLOYERS]: MODEL__NAME__EMPLOYER,
    [COLLECTION__ENGAGEMENTS]: MODEL__NAME__ENGAGEMENT,
    [COLLECTION__JOBS]: MODEL__NAME__JOB
  }[VALUE__COLLECTION];

  const QUERY__INCLUDE__RELATIONS = {
    [COLLECTION__ACCOUNTS]: [
      {
        relation: RELATION__ACCOUNTS_STARRED,
        scope: {
          where: { userId: Core.getUserId() },
          limit: 1,
        },
      },
    ],
    [COLLECTION__CANDIDATES]: [
      'account',
      { engagements: { job: 'employer' } },
      {
        relation: RELATION__CANDIDATES_STARRED,
        scope: {
          where: { accountId: Core.getUserId() },
          limit: 1,
        },
      },
    ],
    [COLLECTION__EMPLOYERS]: [
      { jobs: 'employer' },
      {
        relation: RELATION__EMPLOYERS_STARRED,
        scope: {
          where: { accountId: Core.getUserId() },
          limit: 1,
        },
      },
    ],
    [COLLECTION__ENGAGEMENTS]: [
      {
        relation: RELATION__ENGAGEMENTS_STARRED,
        scope: {
          where: { accountId: Core.getUserId() },
          limit: 1,
        },
      },
      { candidate: 'account' },
      { job: 'employer' },
      'actionOwner',
    ],
    [COLLECTION__JOBS]: [
      'engagements',
      'resumeSubmissionEmailTemplate',
      'jobBlackList',
      'jobSourceList',
      {
        relation: 'employer',
        scope: {
          include: ['employerBlackList', 'employerSourceList'],
        },
      },
      {
        relation: RELATION__JOBS_STARRED,
        scope: {
          where: { accountId: Core.getUserId() },
          limit: 1,
        },
      },
    ],
  }[VALUE__COLLECTION];

  const YES__DISPLAY_VIEWER = YES(state.collection);
  const YES__VALUE_ID = YES(trim(VALUE__ID));
  const YES__VALUE_INPUT = YES(trim(VALUE__INPUT));
  const YES__FETCH_RELATIONS = YES(state.fetchRelations);
  const YES__SUCCESS__FETCH_ENTITY = YES(JSON_DATA_B.id);
  const NOT__SUCCESS__FETCH_ENTITY = YES__VALUE_INPUT && NOT(YES__SUCCESS__FETCH_ENTITY);

  const fetchData = async ({
    id = '',
    fetchRelations = false
  } = {}) => {
    try {
      await updateState({
        jsonDataB: orderJsonProperties({
          data: Obj(
            await readLoopbackRecord({
              collection: VALUE__COLLECTION,
              where: id ? { id } : undefined,
              include: fetchRelations ? QUERY__INCLUDE__RELATIONS : undefined,
              limit: 1
            })
          )
        })
      });
    }
    catch (error) {
      globalErrorHandler(error);
    }
  }

  const onCloseViewer = (event) => {
    event.preventDefault();
    updateState({
      collection: null,
      jsonDataA: null,
      jsonDataB: null,
      valueInput: '',
      fetchRelations: false
    });
  };

  const onChangeInput = async (event) => {
    event.preventDefault();
    const id = Str(event.target.value).replace(/"/g, '').split('/').pop().trim();
    await updateState({
      valueInput: event.target.value,
      id
    });
    clearTimeout(mem.current.timeout);
    mem.current.timeout = setTimeout(() => fetchData({ id, fetchRelations: state.fetchRelations }), 500);
  }

  const onChangeFetchRelations = async (event) => {
    event.preventDefault();
    const fetchRelations = YES(event.target.checked);
    await updateState({ fetchRelations });
    clearTimeout(mem.current.timeout);
    mem.current.timeout = setTimeout(() => fetchData({ id: state.id, fetchRelations }), 500);
  }

  if (YES(global)) {
    setControllerSampleJsonData({
      state,
      updateState,
      fetchData,
      onChangeInput
    });
  }

  return (
    <Box
      acl={Core.isAdmin()}
    >
      <Fieldset
        acl={NOT(global)}
        title={
          <Label className='tt-none'>
            Model JSON data
          </Label>
        }
        subtitle={
          <>
            These are extended models used internally by the frontend.<br />
            Properties starting with an underscore are local-only and not stored in the database.<br />
            Relation objects like account, candidate, etc., are included on request but are not stored directly in the model.
          </>
        }
      >
        <Box column flex1
          style={{ maxWidth: 240 }}
        >
          <Button small fullWidth
            label='Engagement'
            onClick={(event) => {
              event.preventDefault();
              updateState({
                collection: COLLECTION__ENGAGEMENTS,
                jsonDataA: orderJsonProperties({
                  data: getEngagementModel({ extended: true })
                })
              });
            }}
          />
          <Button small fullWidth
            label='Account'
            onClick={(event) => {
              event.preventDefault();
              updateState({
                collection: COLLECTION__ACCOUNTS,
                jsonDataA: orderJsonProperties({
                  data: getAccountModel({ extended: true })
                })
              });
            }}
            className='tt-unset mt-05'
          />
          <Button small fullWidth
            label='Candidate'
            onClick={(event) => {
              event.preventDefault();
              updateState({
                collection: COLLECTION__CANDIDATES,
                jsonDataA: orderJsonProperties({
                  data: getCandidateModel({ extended: true })
                })
              });
            }}
            className='tt-unset mt-05'
          />
          <Button small fullWidth
            label='Employer'
            onClick={(event) => {
              event.preventDefault();
              updateState({
                collection: COLLECTION__EMPLOYERS,
                jsonDataA: orderJsonProperties({
                  data: getEmployerModel({ extended: true })
                })
              });
            }}
            className='tt-unset mt-05'
          />
          <Button small fullWidth
            label='Job'
            onClick={(event) => {
              event.preventDefault();
              updateState({
                collection: COLLECTION__JOBS,
                jsonDataA: orderJsonProperties({
                  data: getJobModel({ extended: true })
                })
              });
            }}
            className='tt-unset mt-05'
          />
        </Box>
      </Fieldset>
      <Dialog alignLeft
        name={joinKeyName([
          'admin tools',
          'dialog__view_sovren_raw_data'
        ])}
        open={YES__DISPLAY_VIEWER}
        onClose={onCloseViewer}
        paperStyle={{ maxWidth: 'calc(100vw - var(--containerMinMargin))', width: '60vw' }}
        title={
          <Box column flex1>
            <Title className='c-purple m-0 mb-1'>
              {NAME__MODEL} JSON data
            </Title>
            <Once
              acl={NOT(global)}
              promise={fetchData}
            />
            <Typography subtitle className='c-gray'>Set a valid ID or URL</Typography>
            <TextField disabledBlurExport
              value={VALUE__INPUT}
              onChange={onChangeInput}
            />
            <Box row className='mt-1'>
              <Box column className='fw-400 f-sm flex-1 nowrap mt-1'>
                <SuccessChipMessage show={YES__SUCCESS__FETCH_ENTITY}>
                  ID: {JSON_DATA_B.id}
                </SuccessChipMessage>
                <WarningChipMessage show={NOT__SUCCESS__FETCH_ENTITY}>
                  {NAME__MODEL} data not found{YES__VALUE_ID ? ` (ID: ${VALUE__ID})` : ''}
                </WarningChipMessage>
              </Box>
              <Checkbox
                label='Include relations'
                checked={YES__FETCH_RELATIONS}
                onChange={onChangeFetchRelations}
              />
            </Box>
          </Box>
        }
        content={
          <Box column flex1>
            <Box row flex1
              className='flex-align-left-top'
            >
              <Fieldset
                title={
                  <Label className='tt-none'>
                    Model
                  </Label>
                }
              >
                <JsonView
                  src={JSON_DATA_A}
                  collapsed={false}
                  className='flex-1 scroll'
                  style={{ maxHeight: 'calc(100vh - 300px)' }}
                />
              </Fieldset>
              <Fieldset
                title={
                  <Label className='tt-none'>
                    Data
                  </Label>
                }
              >
                <JsonView
                  src={JSON_DATA_B}
                  collapsed={false}
                  className='flex-1 scroll'
                  style={{ maxHeight: 'calc(100vh - 300px)' }}
                />
              </Fieldset>
            </Box>
          </Box>
        }
      />
    </Box>
  );
}
